<template>
	<div class="leases-due-utilization-card">
		<div class="leases-due-utilization-card__header">
			<h3 class="leases-due-utilization-card__title">Utilization</h3>
			
			<themed-tooltip 
				placement="bottom"
				:show-delay="300"
				max-width="300px"
			>
				<button 
					class="leases-due-utilization-card__info" 
					aria-label="More information about Utilization"
					@mouseenter="loadTooltip('dashboard-leasesdue-utilization')"
				>
					<svg 
						width="14" 
						height="14" 
						viewBox="0 0 24 24" 
						fill="none" 
						stroke="currentColor" 
						stroke-width="2" 
						stroke-linecap="round" 
						stroke-linejoin="round"
					>
						<circle cx="12" cy="12" r="10"></circle>
						<line x1="12" y1="16" x2="12" y2="12"></line>
						<line x1="12" y1="8" x2="12.01" y2="8"></line>
					</svg>
				</button>
				<template #content>
					<div v-html="getTooltipContent('dashboard-leasesdue-utilization')"></div>
				</template>
			</themed-tooltip>
		</div>
		
		<div class="leases-due-utilization-card__content">
			<!-- Circular progress indicator -->
			<div class="leases-due-utilization-card__chart">
				<svg class="leases-due-utilization-card__circle" viewBox="0 0 120 120">
					<circle
						class="leases-due-utilization-card__circle-bg"
						cx="60"
						cy="60"
						r="50"
						fill="none"
						stroke-width="12"
					/>
					<circle
						class="leases-due-utilization-card__circle-progress"
						cx="60"
						cy="60"
						r="50"
						fill="none"
						stroke-width="12"
						:stroke-dasharray="circumference"
						:stroke-dashoffset="progressOffset"
					/>
				</svg>
				<div class="leases-due-utilization-card__percentage">{{ averageUtilization }}%</div>
			</div>
			
			<div class="leases-due-utilization-card__label">AVG UTILIZATION</div>
			
			<!-- Property type breakdown. Only types the portfolio actually holds:
			     five hardcoded rows meant a pure office/industrial/retail portfolio
			     still advertised "Mixed Use 0%" and "Warehouse 0%", which reads as
			     broken rather than as absent. -->
			<div class="leases-due-utilization-card__breakdown">
				<div
					v-for="type in propertyTypeBreakdown"
					:key="type.name"
					class="leases-due-utilization-card__breakdown-item"
				>
					<div class="leases-due-utilization-card__breakdown-left">
						<span
							class="leases-due-utilization-card__dot"
							:class="`leases-due-utilization-card__dot--${type.colour}`"
						></span>
						<span class="leases-due-utilization-card__type-name">{{ type.name }}</span>
					</div>
					<span class="leases-due-utilization-card__type-value">{{ type.utilization }}%</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment';
import ThemedTooltip from '../ThemedTooltip.vue';
import axios from 'axios';

export default {
	name: 'LeasesDueUtilizationCard',
	components: {
		ThemedTooltip
	},
	props: {
		siteData: {
			type: Array,
			required: true
		},
		selectedYears: {
			type: Number,
			default: 3
		}
	},
	data() {
		return {
			tooltipCache: {}
		}
	},
	computed: {
		availableSites() {
			return this.siteData.filter(site => 
				site.archived_at == null && moment(site.lease_end_date).isAfter(moment())
			);
		},
		filteredSites() {
			const months = this.selectedYears * 12;
			return this.availableSites.filter(site =>
				moment(site.lease_end_date).isBefore(moment().add(months, 'M'))
			);
		},
		
		// Average utilization for filtered sites
		averageUtilization() {
			if (this.filteredSites.length === 0) {
				return 0;
			}
			
			const totalSize = _.sumBy(this.filteredSites, 'size') || 0;
			if (totalSize === 0) {
				return 0;
			}
			
			const usedSquareFootage = _.sumBy(this.filteredSites, site => 
				site.size * ((site.utilization || 0) / 100)
			);
			
			return Math.round((usedSquareFootage / totalSize) * 100);
		},
		
		/**
		 * The property types this portfolio actually holds, in the usual reading
		 * order, with anything unrecognised appended alphabetically.
		 *
		 * Built from the sites rather than from a fixed list of five, so a portfolio
		 * with no warehouses does not carry a "Warehouse 0%" row. A type stays in the
		 * list at 0% if it has sites but no utilisation recorded — that is a real
		 * reading, unlike a type nobody owns.
		 */
		propertyTypeBreakdown() {
			const order = ['Office', 'Retail', 'Industrial', 'Mixed Use', 'Warehouse'];
			const colours = {
				'Office': 'blue',
				'Retail': 'green',
				'Industrial': 'orange',
				'Mixed Use': 'purple',
				'Warehouse': 'gray',
			};

			const present = _.uniq(
				this.filteredSites
					.map(site => site && site.building && site.building.type ? site.building.type.type : null)
					.filter(Boolean)
			);

			const rank = name => {
				const i = order.indexOf(name);
				return i === -1 ? order.length : i;
			};

			return present
				.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
				.map(name => ({
					name,
					colour: colours[name] || 'gray',
					utilization: this.calculatePropertyTypeUtilization(name),
				}));
		},

		// Circular progress chart properties
		circumference() {
			// Calculate circle circumference: 2 * π * radius (radius = 50)
			return 2 * Math.PI * 50;
		},
		
		progressOffset() {
			// Calculate stroke-dashoffset for progress arc
			return this.circumference - (this.circumference * this.averageUtilization / 100);
		}
	},
	methods: {
		calculatePropertyTypeUtilization(propertyType) {
			// Filter filtered sites by property type with null safety
			const sitesOfType = this.filteredSites.filter(site => {
				if (!site || !site.building || !site.building.type) {
					return false;
				}
				return site.building.type.type === propertyType;
			});
			
			// Return 0 if no sites of this type
			if (sitesOfType.length === 0) {
				return 0;
			}
			
			// Filter out sites with zero square footage
			const validSites = sitesOfType.filter(site => site.size > 0);
			
			if (validSites.length === 0) {
				return 0;
			}
			
			// Calculate total square footage
			const totalSquareFootage = _.sumBy(validSites, 'size');
			
			if (totalSquareFootage === 0) {
				return 0;
			}
			
			// Calculate weighted average utilization
			const weightedUtilization = _.sumBy(validSites, site => {
				const utilization = site.utilization || 0;
				return (site.size * utilization) / 100;
			});
			
			return Math.round((weightedUtilization / totalSquareFootage) * 100);
		},
		
		getTooltipContent(identifier) {
			if (this.tooltipCache[identifier]) {
				return this.tooltipCache[identifier];
			}
			return '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>';
		},
		
		async loadTooltip(identifier) {
			if (!this.tooltipCache[identifier]) {
				this.$set(this.tooltipCache, identifier, '<div style="display: flex; align-items: center; gap: 0.5rem;"><svg class="animate-spin" style="width: 1rem; height: 1rem;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg><span>Loading...</span></div>');
				
				try {
					const response = await axios.get(`/api/tooltips/identifier/${identifier}`);
					this.$set(this.tooltipCache, identifier, response.data.content || 'Tooltip content not available');
				} catch (err) {
					console.error('Error loading tooltip:', err);
					if (err.response && err.response.status === 404) {
						this.$set(this.tooltipCache, identifier, 'Tooltip content is not yet available for this field.');
					} else {
						this.$set(this.tooltipCache, identifier, 'Failed to load information');
					}
				}
			}
		}
	}
}
</script>
